import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import {
  View,
  ActivityIndicator,
  StyleSheet,
  ScrollView,
  SafeAreaView,
  TouchableOpacity,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { HeaderBackButton } from '@react-navigation/elements';
import { getCartView, getTravelCart, addTripName } from '../store/actions/search';
import { Screens } from '../constants/ScreenNames';
import { CommonProperties, CommonStyles, Color } from '../styles';
import TripSummary from '../components/TripSummary';
import BaseText from '../components/BaseText';
import { movePrevious } from '../store/actions/workflow';
import BottomButton from '../components/BottomButton';
import {
  selectFirstAndLastDateOfSearchCriteria,
  selectFirstAndLastDateOfSearchCriteriaMultiCity,
  selectIsMultiCity,
  selectChangeRes,
  selectIsOnlyHotelMultiLegTrip,
} from '../store/selectors/search.selector';
import { openAlert } from '../store/actions/alert';
import HeaderTitle from '../components/shared/HeaderTitle';
import FloatButton from '../components/FloatButton';
import InputDialog from '../components/InputDialog';
import SearchFabMenuModal from '../components/SearchFabMenuModal';
import moment from 'moment';
import { rightAgentHeaderOption } from '../utils/defaultNavOptions';
import useBoolean from '../utils/useBoolean';
import { useTranslation } from 'react-i18next';
import { useComponentTKey, i18nNS } from '../i18n';
import { useDateFormat } from '../utils/useDateFormat';
import Icon from '../components/Icon';
import RowView from '../components/RowView';
import { selectedCarComponents } from '../store/selectors/car.selector';

const TripSummaryScreen = ({ navigation }) => {
  const { t } = useTranslation(i18nNS.checkout);
  const { tKey } = useComponentTKey('TripSummary');
  const dispatch = useDispatch();
  const [cartViewLoading, setCartViewLoading] = useState(false);
  const user = useSelector((state) => state && state.auth && state.auth.user);
  const userId = useSelector(
    (state) => state && state.auth.tokenData && state.auth.tokenData.userId
  );
  const selectedCar = useSelector(selectedCarComponents);
  const changeResData = useSelector(selectChangeRes);
  const tripSummary = useSelector((state) => state && state.search && state.search.tripSummary);
  const searchCriteria = useSelector(selectFirstAndLastDateOfSearchCriteria);
  const searchCriteriaMulti = useSelector(selectFirstAndLastDateOfSearchCriteriaMultiCity);
  const isMultiCity = useSelector(selectIsMultiCity);
  const isOnlyMultiCityHotel = useSelector(selectIsOnlyHotelMultiLegTrip);

  const [defaultName, setDefaultName] = useState('');
  const [isEditName, setIsEditName] = useState(false);
  const [modalVisible, showModal, hideModal] = useBoolean(false);
  const nameInput = useRef(null);
  const formatDate = useDateFormat();

  useLayoutEffect(() => {
    navigation.setOptions({
      headerLeft: (headerLeftProps) => (
        <HeaderBackButton {...headerLeftProps} onPress={() => dispatch(movePrevious())} />
      ),
      headerTitle: () => (
        <HeaderTitle
          title={
            (isMultiCity
              ? isOnlyMultiCityHotel
                ? t(tKey('MultiHotelStop'))
                : t(tKey('MultiStop'))
              : tripSummary?.Header) || t(tKey('TripSummary'))
          }
          subtitle={
            isMultiCity
              ? moment(searchCriteriaMulti?.fromDate).format('MMM DD') +
                (searchCriteriaMulti?.fromDate !== searchCriteriaMulti?.toDate
                  ? ' - ' + moment(searchCriteriaMulti?.toDate).format('MMM DD')
                  : '')
              : formatDate(searchCriteria?.fromDate) +
                (searchCriteria?.fromDate !== searchCriteria?.toDate
                  ? ' - ' + formatDate(searchCriteria?.toDate)
                  : '')
          }
        />
      ),
      ...rightAgentHeaderOption({ navigation }),
      headerTitleAlign: 'center',
    });
  }, [
    navigation,
    dispatch,
    searchCriteria,
    searchCriteriaMulti,
    tripSummary,
    isMultiCity,
    t,
    tKey,
    formatDate,
  ]);

  useEffect(() => {
    if (tripSummary) {
      let name = '';
      if (userId.toString() !== '0' && user) {
        name += `${user.FirstName}'s `;
      }
      name += `${tripSummary.DestinationName || tripSummary.Header || ''} ${t(tKey('Trip'))}`;

      setDefaultName(name);
    }
  }, [tripSummary, user, userId, t, tKey]);

  useEffect(() => {
    async function fetchCartView() {
      setCartViewLoading(true);
      if (changeResData) {
        await dispatch(getCartView(changeResData));
      } else {
        await dispatch(getCartView(null, selectedCar));
      }
      setCartViewLoading(false);
    }
    fetchCartView();
  }, [changeResData, dispatch]);

  useEffect(() => {
    if (nameInput && nameInput.current) {
      nameInput.current.focus();
    }
  }, [isEditName]);

  return (
    <SafeAreaView style={styles.container}>
      {cartViewLoading ? (
        <ActivityIndicator size="small" color={Color.primary} />
      ) : (
        <View style={styles.wrapper}>
          <ScrollView style={CommonStyles.flex1}>
            <View style={styles.summary}>
              {tripSummary?.IsTripNameEditOnTripSummary && (
                <View style={styles.name}>
                  <InputDialog
                    label="Change Trip Name" // #i18n
                    defaultValue={defaultName}
                    visible={isEditName}
                    onCancel={() => setIsEditName(false)}
                    onSubmit={(text) => {
                      setIsEditName(false);
                      setDefaultName(text);
                    }}
                  />
                  <TouchableOpacity onPress={() => setIsEditName(true)}>
                    <RowView>
                      <BaseText style={CommonStyles.h3}>{defaultName}</BaseText>
                      <Icon name="edit" size={18} color={Color.primary} style={CommonStyles.ml5} />
                    </RowView>
                  </TouchableOpacity>
                </View>
              )}
              <TripSummary
                changeResFare={tripSummary?.ChangeTripViewModel}
                changeResData={changeResData}
              />
            </View>
          </ScrollView>
          <FloatButton onPress={showModal} iconName="menu" />
          <SearchFabMenuModal navigate={navigation} onClose={hideModal} visible={modalVisible} />
          <BottomButton
            onPress={() => {
              if (tripSummary?.ChangeTripViewModel) {
                if (tripSummary?.ChangeTripViewModel?.ExchangePriceRQError) {
                  dispatch(
                    openAlert({ title: tripSummary?.ChangeTripViewModel.ExchangePriceRQError })
                  );
                  return;
                }
              }
              if (tripSummary?.ChangeTripViewModel) {
                const totalCalculatedFare = tripSummary?.ChangeTripViewModel?.TotalFair;
                dispatch(getTravelCart(totalCalculatedFare));
              } else {
                dispatch(getTravelCart());
              }
              dispatch(addTripName(defaultName));
              navigation.navigate(Screens.Passenger);
            }}
            label={!changeResData ? t(tKey('Book')) : 'Accept Change'}
          />
        </View>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Color.accent,
  },
  wrapper: {
    height: '100%',
    justifyContent: 'space-between',
  },
  summary: {
    ...CommonProperties.pt2,
    ...CommonProperties.px3,
  },
  name: {
    ...CommonProperties.py3,
    borderBottomColor: Color.disabled,
    borderBottomWidth: 0.5,
  },
});

export default TripSummaryScreen;
